/**
 * The base prices the business is currently charging, for the "Book from $X"
 * teaser.
 *
 * The landing page used to price this entirely from `@homero/pricing`'s bundled
 * rate card. That was correct while the prices lived in code and wrong the
 * moment an admin could change them: every edit in the pricing editor opened a
 * gap between the price advertised here and the price quoted one click later in
 * the booking funnel, and the gap grew with each change. `/pricing/base-prices`
 * exists so this page can ask without pulling in an Apollo client or the full
 * config projection.
 *
 * Failure is silent and falls back to the bundled card. This is a teaser, not a
 * quote — nothing is charged from it, the booking app fetches its own rates and
 * blocks checkout if it cannot, and a marketing page that renders no price at
 * all because an endpoint was slow is a worse outcome than one showing a price
 * from last week.
 */

import { useEffect, useState } from 'react';
import { CleaningTypeId, MAX_ROOMS, RoomPrices } from '@homero/pricing';

export interface BasePrices {
  /** Base price per cleaning type, in integer cents. */
  cleaningTypes: Partial<Record<CleaningTypeId, number>>;
  /**
   * What each bedroom and bathroom adds, in integer cents, by position. Null
   * when the server did not send a usable table, in which case the bundled
   * card's flat table stays.
   */
  roomCents: RoomPrices | null;
}

interface BasePricesResponse {
  cleaningTypes: Array<{ cleaningType: CleaningTypeId; priceCents: number }>;
  roomCents?: RoomPrices;
}

/**
 * The room table, if the server sent a whole one.
 *
 * Absent from a server that predates configurable room prices — and that
 * server charges the bundled flat table, so falling back to it advertises the
 * right price. A short or malformed table is dropped the same way rather than
 * handed to the engine, which refuses to price a room count it has no entry for.
 */
function usableRoomTable(prices: RoomPrices | undefined): RoomPrices | null {
  const whole = (table: unknown): table is number[] =>
    Array.isArray(table) &&
    table.length === MAX_ROOMS &&
    table.every((cents) => Number.isInteger(cents) && cents >= 0);
  return prices && whole(prices.bedroom) && whole(prices.bathroom) ? prices : null;
}

export function useBasePrices(): BasePrices | null {
  const [prices, setPrices] = useState<BasePrices | null>(null);

  useEffect(() => {
    let cancelled = false;

    fetch(`${process.env.REACT_APP_API_URL}/pricing/base-prices`)
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.json() as Promise<BasePricesResponse>;
      })
      .then((body) => {
        if (cancelled || !Array.isArray(body?.cleaningTypes)) return;
        const cleaningTypes: Partial<Record<CleaningTypeId, number>> = {};
        for (const entry of body.cleaningTypes) {
          if (typeof entry?.priceCents === 'number') {
            cleaningTypes[entry.cleaningType] = entry.priceCents;
          }
        }
        setPrices({ cleaningTypes, roomCents: usableRoomTable(body.roomCents) });
      })
      .catch(() => {
        // Bundled card stays. See the note at the top of this file.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return prices;
}
